/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import EmailInput from "../form/email-input";
import PasswordInput from "../form/password-input";
import { Button, notification } from "antd";
import { Link } from "react-router-dom";
import CustomInput from "../form/custom-input";
import PhoneNumberInput from "../form/phone-number";
import { formatTime } from "../../utils";
import { TagOutlined } from "@ant-design/icons";
import DataProvider from "../../services";

export default function UserRegister(props: any) {
  const [api, contextHolder] = notification.useNotification();
  const [isActive, setIsActive] = useState(true);
  const [time, setTime] = useState(120);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [localCouponInput, setLocalCouponInput] = useState<string>("");
  const [localAppliedCoupon, setLocalAppliedCoupon] = useState<string | null>(null);

  const couponInput = props.couponInput !== undefined ? props.couponInput : localCouponInput;
  const setCouponInput = props.setCouponInput ?? setLocalCouponInput;
  const appliedCoupon = props.appliedCoupon !== undefined ? props.appliedCoupon : localAppliedCoupon;
  const setAppliedCoupon = props.setAppliedCoupon ?? setLocalAppliedCoupon;

  const handleApplyCoupon = async () => {
    const code = couponInput?.trim().toUpperCase();
    if (!code) {
      api.warning({ message: "Promo code", description: "Enter a promo code." });
      return;
    }
    setValidatingCoupon(true);
    try {
      const result = await DataProvider.validateCoupon(code);
      if (result.valid) {
        setAppliedCoupon(code);
        api.success({
          message: "Coupon activated successfully",
          description: "Go ahead to signup.",
          duration: 4,
        });
      } else {
        api.error({
          message: "Invalid coupon",
          description: result.message ?? "This coupon is not valid.",
        });
      }
    } catch (_) {
      api.error({
        message: "Invalid coupon",
        description: "Could not validate coupon. Please check the code and try again.",
      });
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
  };

  const startCountdown = () => {
    setIsActive(true);
    setTime(120);
  };

  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => {
      setTime((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(interval);
          setIsActive(false);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive]);

  useEffect(() => {
    startCountdown();
  }, []);

  return (
    <div className="w-full h-full flex flex-col">
      <div className="w-full">
        {contextHolder}
        {props.isOtpSent ? (
          <div className="w-full space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Verification Code
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                A verification code has been sent to your{" "}
                {props.signupMethod === "email" ? "email" : "phone"}. Please
                enter it below.
              </p>
              <input
                maxLength={6}
                className="w-full h-14 px-4 rounded-xl border-2 border-gray-200 focus:border-[#1A94EC] focus:ring-4 focus:ring-[#1A94EC]/10 outline-none transition-all text-center text-xl tracking-[0.5em] font-semibold"
                type="text"
                value={props.otp}
                onChange={(e) => props.setOtp(e.target.value)}
                placeholder="000000"
              />
            </div>
            <Button
              type="primary"
              onClick={props.onSubmit}
              size="large"
              className="bg-gradient-to-r from-[#1A94EC] to-[#0d7bc4] w-full h-14 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-[#0d7bc4] hover:to-[#1A94EC] transition-all text-base"
              loading={props.loading}
            >
              {props.loading ? "Verifying..." : "Verify OTP"}
            </Button>
            <div className="flex gap-3">
              <Button
                disabled={isActive}
                onClick={props.sendOtp}
                size="large"
                className="flex-1 h-12 rounded-xl"
              >
                {isActive
                  ? `Resend in ${formatTime(time)}`
                  : "Resend Code"}
              </Button>
              <Button
                onClick={() => {
                  props.setIsOtpSent(false);
                  props.setOtp("");
                }}
                size="large"
                className="flex-1 h-12 rounded-xl"
              >
                {props.signupMethod === "email" ? "Change Email" : "Change Number"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="w-full space-y-6">
            {/* Signup Method Toggle */}
            <div className="flex gap-2 p-1 bg-gradient-to-r from-gray-100 to-gray-50 rounded-2xl border border-gray-200">
              <button
                type="button"
                onClick={() => props.handleSignupMethodChange("email")}
                className={`flex-1 py-3 px-4 rounded-xl text-base font-semibold transition-all duration-300 ${
                  props.signupMethod === "email"
                    ? "bg-white text-[#1A94EC] shadow-lg transform scale-[1.02]"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                Email
              </button>
              <button
                type="button"
                onClick={() => props.handleSignupMethodChange("phone")}
                className={`flex-1 py-3 px-4 rounded-xl text-base font-semibold transition-all duration-300 ${
                  props.signupMethod === "phone"
                    ? "bg-white text-[#1A94EC] shadow-lg transform scale-[1.02]"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                Phone Number
              </button>
            </div>

            {props.signupMethod === "email" && (
              <div className="w-full space-y-5">
                <CustomInput
                  title="Full name"
                  placeholder="Enter your full name"
                  value={props.name}
                  onChangeText={props.setName}
                />
                <EmailInput
                  email={props.email}
                  setEmail={props.setEmail}
                  placeholder="Enter your email address"
                />
                <PasswordInput
                  required
                  password={props.password}
                  setPassword={props.setPassword}
                  placeholder="Enter your password"
                />
                <CustomInput
                  title="Referral Code (Optional)"
                  placeholder="Enter referral code"
                  value={props.referralCode}
                  onChangeText={props.setReferralCode}
                />
                <div className="w-full my-4">
                  <div className="my-2">
                    <p className="mb-1 md:text-base text-sm">
                      <TagOutlined className="mr-1" aria-hidden />
                      Apply promo code
                    </p>
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between rounded-lg border border-solid border-green-200 bg-green-50 px-2 py-2 h-10 md:h-12">
                        <span className="md:text-base text-sm text-green-800">
                          {appliedCoupon}
                        </span>
                        <Button
                          type="link"
                          size="small"
                          onClick={handleRemoveCoupon}
                          className="text-gray-600 p-0 h-auto text-sm md:text-base"
                          aria-label="Remove coupon"
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <div className="flex w-full rounded-lg border border-solid border-gray-200 overflow-hidden">
                        <input
                          id="register-coupon-input-email"
                          type="text"
                          placeholder="Enter coupon code"
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                          maxLength={64}
                          className="h-10 md:h-12 flex-1 min-w-0 rounded-none border-0 px-2 md:text-base text-sm bg-white outline-none"
                          aria-label="Coupon code"
                        />
                        <button
                          type="button"
                          onClick={handleApplyCoupon}
                          disabled={validatingCoupon}
                          className="h-10 md:h-12 px-4 border-l border-solid border-gray-200 bg-gray-50 text-gray-700 md:text-base text-sm font-medium hover:bg-gray-100 shrink-0 disabled:opacity-70"
                          aria-label="Apply coupon"
                        >
                          {validatingCoupon ? "Checking…" : "Apply"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {appliedCoupon && props.subscriptionMsisdn !== undefined && (
                  <div className="w-full mt-2">
                    <PhoneNumberInput
                      title="Phone number (for mCare subscription)"
                      value={props.subscriptionMsisdn}
                      onChangeText={props.setSubscriptionMsisdn}
                      placeholder="0240005678"
                      notes="Used to subscribe you on mCare with your promo code."
                    />
                  </div>
                )}

                <Button
                  type="primary"
                  size="large"
                  onClick={props.onSubmit}
                  className="bg-gradient-to-r from-[#1A94EC] to-[#0d7bc4] w-full h-14 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-[#0d7bc4] hover:to-[#1A94EC] transition-all text-base mt-2"
                  loading={props.loading}
                >
                  {props.loading ? "Sending code..." : "Sign up"}
                </Button>

                <div className="w-full flex justify-center items-center mt-6">
                  <p className="text-gray-600 text-base">
                    Already have an account?{" "}
                    <Link to="/sign-in" className="text-[#1A94EC] font-semibold hover:text-[#0d7bc4] hover:underline transition-all">
                      Sign in
                    </Link>
                  </p>
                </div>
              </div>
            )}

            {props.signupMethod === "phone" && (
              <div className="w-full space-y-5">
                <CustomInput
                  title="Full name"
                  placeholder="Enter your full name"
                  value={props.name}
                  onChangeText={props.setName}
                />
                <PhoneNumberInput
                  required
                  title="Phone Number"
                  value={props.phoneNumber}
                  onChangeText={props.setPhoneNumber}
                  placeholder="0240005678"
                  notes="This number will also be used for your subscription. It is advisable to use a number with MoMo."
                />
                <PasswordInput
                  required
                  password={props.password}
                  setPassword={props.setPassword}
                  placeholder="Enter your password"
                />
                <CustomInput
                  title="Referral Code (Optional)"
                  placeholder="Enter referral code"
                  value={props.referralCode}
                  onChangeText={props.setReferralCode}
                />
                <div className="w-full my-4">
                  <div className="my-2">
                    <p className="mb-1 md:text-base text-sm">
                      <TagOutlined className="mr-1" aria-hidden />
                      Apply promo code
                    </p>
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between rounded-lg border border-solid border-green-200 bg-green-50 px-2 py-2 h-10 md:h-12">
                        <span className="md:text-base text-sm text-green-800">
                          {appliedCoupon}
                        </span>
                        <Button
                          type="link"
                          size="small"
                          onClick={handleRemoveCoupon}
                          className="text-gray-600 p-0 h-auto text-sm md:text-base"
                          aria-label="Remove coupon"
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <div className="flex w-full rounded-lg border border-solid border-gray-200 overflow-hidden">
                        <input
                          id="register-coupon-input-phone"
                          type="text"
                          placeholder="Enter coupon code"
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                          maxLength={64}
                          className="h-10 md:h-12 flex-1 min-w-0 rounded-none border-0 px-2 md:text-base text-sm bg-white outline-none"
                          aria-label="Coupon code"
                        />
                        <button
                          type="button"
                          onClick={handleApplyCoupon}
                          disabled={validatingCoupon}
                          className="h-10 md:h-12 px-4 border-l border-solid border-gray-200 bg-gray-50 text-gray-700 md:text-base text-sm font-medium hover:bg-gray-100 shrink-0 disabled:opacity-70"
                          aria-label="Apply coupon"
                        >
                          {validatingCoupon ? "Checking…" : "Apply"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <Button
                  type="primary"
                  size="large"
                  onClick={props.onSubmit}
                  className="bg-gradient-to-r from-[#1A94EC] to-[#0d7bc4] w-full h-14 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-[#0d7bc4] hover:to-[#1A94EC] transition-all text-base mt-2"
                  loading={props.loading}
                >
                  {props.loading ? "Sending OTP..." : "Sign up"}
                </Button>

                <div className="w-full flex justify-center items-center mt-6">
                  <p className="text-gray-600 text-base">
                    Have an account?{" "}
                    <Link to="/sign-in" className="text-[#1A94EC] font-semibold hover:text-[#0d7bc4] hover:underline transition-all">
                      Sign in
                    </Link>
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
